import { inject, injectable } from '../../modules/common/di';
import { BaseService } from './baseService';
import { AuthService } from './authService';
import { ReportsAdminApi } from '../api/admin/reports/reportsApi';
import { GetAvailableReportsApiResponse, ReportDeliveryType } from '../api/admin/reports/getAvailableReportsApiResponse';
import { GetReportExecutionsApiResponse } from '../api/admin/reports/getReportExecutionsApiResponse';
import { GetReportGroupsApiResponse } from '../api/admin/reports/getReportGroupsApiResponse';
import { ApiResponseBase } from '../models/apiResponseBase';
import { GetSystemStatusApiResponse } from '../api/admin/reports/getSystemStatusApiResponse';
import { GetReportCollectionsApiResponse } from '../api/admin/reports/getReportCollectionsApiResponse';
import { CreateReportCollectionApiResponse, CreateReportCollectionModel } from '../api/admin/reports/createReportCollectionApiResponse';
import { UpdateReportCollectionApiResponse, UpdateReportCollectionModel } from '../api/admin/reports/updateReportCollectionApiResponse';
import { ReportResponseFormat } from '../api/admin/reports/getReportDataApiResponse';

@injectable('ReportsService')
export class ReportsService extends BaseService {
  @inject('AuthService') protected readonly authService!: AuthService;
  @inject('ReportsAdminApi') protected readonly reportsAdminApi!: ReportsAdminApi;

  constructor() {
    super();
  }

  /**
   * Get report groups.
   * Returns a list of report groups available for the organization.
   *
   * @param {number} organizationId Mandatory organization ID.
   * @param [params] Requested parameters.
   * @param {boolean} [params.analytic] Return only groups, which include analytic reports.
   * @param {boolean} [params.all] Return all groups including not assigned to the organization (default is true).
   * @returns {Promise<GetReportGroupsApiResponse>}
   */
  getReportGroups(organizationId: number, params?: {
    analytic?: boolean;
    all?: boolean;
  }): Promise<GetReportGroupsApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getReportGroups(organizationId, params));
  }

  /**
   * Set report group.
   * Enables report group for the specific organization.
   *
   * @param {number} organizationId Mandatory organization ID to status for.
   * @param {number} reportGroupId Reports group ID.
   * @param {number} [notificationGroupId] Organization notification group ID.
   * @returns {Promise<ApiResponseBase>}
   */
  setReportGroup(
    organizationId: number,
    reportGroupId: number,
    notificationGroupId?: number
  ): Promise<ApiResponseBase> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }
    if (reportGroupId < 1 || isNaN(reportGroupId)) {
      return this.reject(`Report group ID is incorrect [${reportGroupId}].`);
    }

    const params = {
      reportGroupId: reportGroupId,
      notificationGroupId: notificationGroupId,
    };

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.setReportGroup(organizationId, params));
  }

  /**
   * Delete report group.
   * Disables report group for the specific organization.
   *
   * @param {number} organizationId Mandatory organization ID to status for.
   * @param {number} reportGroupId Reports group ID.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteReportGroup(
    organizationId: number,
    reportGroupId: number,
  ): Promise<ApiResponseBase> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }
    if (reportGroupId < 1 || isNaN(reportGroupId)) {
      return this.reject(`Report group ID is incorrect [${reportGroupId}].`);
    }

    const params = {
      reportGroupId: reportGroupId,
    };

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.deleteReportGroup(organizationId, params));
  }

  /**
   * Get available reports.
   * This API provide information about available reports.
   *
   * @param [params] Requested parameters.
   * @param {number} [params.reportId] Optional filter by report ID.
   * @param {number} [params.reportGroupId] Optional filter by report group ID.
   * @param {number} [params.organizationId] Optional filter by organization ID.
   * @param {boolean} [params.analytic] Optional filter by 'analytic' report flag.
   * @returns {Promise<GetAvailableReportsApiResponse>}
   */
  getAvailableReports(params?: {
    reportId: number;
    reportGroupId: number;
    organizationId: number;
    analytic: boolean;
  }): Promise<GetAvailableReportsApiResponse> {
    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getAvailableReports(params));
  }

  /**
   * Generate specific report.
   * The result can provided in CSV or HTML format. Long generated reports can be delivered by email.
   * All report parameters should be provided in the query string.
   *
   * @param params Requested parameters.
   * @param {number} params.reportId Report ID.
   * @param {ReportDeliveryType} params.deliveryType Way to return report data:
   *  2 - sent result by email
   *  3 - save result to be retrieved later
   * @param {number} [params.organizationId] Generate report for specific organization.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  generateReport(params: {
    reportId: number;
    deliveryType: ReportDeliveryType;
    organizationId?: number;
    [reportParamName: string]: string | number | boolean | undefined;
  }): Promise<ArrayBuffer | string | object> {
    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.generateReport(params));
  }

  /**
   * Get report executions for report group.
   * Report mode returns executions for a specific report and report group.
   * Reports are executed according to the schedules defined for the report groups to which they belong.
   *
   * @param params Requested parameters.
   * @param {string|number} params.startDate Mandatory start date.
   * @param {string|number} params.endDate Mandatory end date.
   * @param {number} params.reportId Report ID.
   * @param {number} params.reportGroupId Report group ID.
   * @param {number} [params.organizationId] Organization ID, required for organizational report group.
   * @param {string} [params.sortBy] Field name to sort by.
   * @param {'asc'|'desc'} [params.sortOrder] Sort order, default is ascending.
   * @returns {Promise<GetReportExecutionsApiResponse>}
   */
  getReportExecutions(params: {
    startDate: number | string;
    endDate: number | string;
    reportId: number;
    reportGroupId: number;
    organizationId?: number;
    demandUserId?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<GetReportExecutionsApiResponse>;

  /**
   * Get report executions for report collection.
   * Collection mode returns executions for all reports in the given report collection.
   * Reports are executed according to the schedule defined in report collection.
   *
   * @param params Requested parameters.
   * @param {string|number} params.startDate Mandatory start date.
   * @param {string|number} params.endDate Mandatory end date.
   * @param {number} params.organizationId Mandatory organization ID.
   * @param {number} params.collectionId Report collection ID.
   * @param {string} [params.sortBy] Field name to sort by.
   * @param {'asc'|'desc'} [params.sortOrder] Sort order, default is ascending.
   * @returns {Promise<GetReportExecutionsApiResponse>}
   */
  getReportExecutions(params: {
    startDate: number | string;
    endDate: number | string;
    organizationId: number;
    collectionId: number;
    demandUserId?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<GetReportExecutionsApiResponse>;

  /**
   * Get list of report executions.
   * Could be a report mode or collection mode depends on the provided parameters.
   *
   * @param params Requested parameters.
   * @returns {Promise<GetReportExecutionsApiResponse>}
   */
  getReportExecutions(params: {
    startDate: number | string;
    endDate: number | string;
    reportId?: number;
    reportGroupId?: number;
    organizationId?: number;
    collectionId?: number;
    demandUserId?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<GetReportExecutionsApiResponse> {
    if (params && params.organizationId && (params.organizationId < 1 || isNaN(params.organizationId))) {
      return this.reject(`Organization ID is incorrect [${params.organizationId}].`);
    }

    const parameters = {
      ...params,
      sortCollection: 'executions',
      sortBy: params.sortBy || 'executionDateMs',
      sortOrder: params.sortOrder || 'desc',
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getReportExecutionData(parameters));
  }

  /**
   * Get report data.
   * This API returns report data by the token generated during report generation.
   *
   * @param {string} token JWT generated during report generation.
   * @param {ReportResponseFormat} [responseFormat] Report data response format.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  getReportData(token: string, responseFormat?: ReportResponseFormat): Promise<ArrayBuffer | string | object> {
    if (!token) {
      return this.reject('Report token is missing.');
    }

    const params: { responseFormat?: ReportResponseFormat } = {};
    if (responseFormat !== undefined) {
      params.responseFormat = responseFormat;
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getReportData(token, params));
  }

  /**
   * System status.
   * This API return detailed statistics about different system componets for the last 24 hours.
   *
   * @param {number} [organizationId] Organization ID.
   * @returns {Promise<GetSystemStatusApiResponse>}
   */
  getSystemStatus(organizationId?: number): Promise<GetSystemStatusApiResponse> {
    if (organizationId && (organizationId < 1 || isNaN(organizationId))) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    const params = {
      organizationId: organizationId,
    };

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getSystemStatus(params));
  }

  // #region Organization Reports

  /**
   * Get report collections for organization.
   * If collectionId is specified, returns only the matching collection. If reportId is specified, returns only collections that contain the given report.
   *
   * @param {number} organizationId Organization ID.
   * @param {object} [params] Optional parameters.
   * @param {number} [params.collectionId] Optional report collection ID.
   * @param {number} [params.reportId] Optional report ID.
   * @returns {Promise<GetReportCollectionsApiResponse>}
   */
  getReportCollections(organizationId: number, params?: {
    collectionId?: number;
    reportId?: number;
  }): Promise<GetReportCollectionsApiResponse> {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is missing or incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.getReportCollections(organizationId, params));
  }

  /**
   * Create report collection in organization.
   * Optionally, a list of reports can be included. Each report must have a reportId.
   * The combination of reportId and parameters must be unique across the collection.
   *
   * @param {number} organizationId Organization ID.
   * @param {CreateReportCollectionModel} model Report collection model.
   * @returns {Promise<CreateReportCollectionApiResponse>}
   */
  createReportCollection(organizationId: number, model: CreateReportCollectionModel): Promise<CreateReportCollectionApiResponse> {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is missing or incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.createReportCollection(organizationId, model));
  }

  /**
   * Update report collection in organization.
   * All request body fields are optional. Fields that are not provided retain their existing values.
   *
   * @param {number} organizationId Organization ID.
   * @param {number} collectionId Report collection ID.
   * @param {UpdateReportCollectionModel} model Report collection model for update.
   * @returns {Promise<UpdateReportCollectionApiResponse>}
   */
  updateReportCollection(organizationId: number, collectionId: number, model: UpdateReportCollectionModel): Promise<UpdateReportCollectionApiResponse> {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is missing or incorrect [${organizationId}].`);
    }
    if (!collectionId || collectionId < 1 || isNaN(collectionId)) {
      return this.reject(`Collection ID is missing or incorrect [${collectionId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.updateReportCollection(organizationId, collectionId, model));
  }

  /**
   * Delete report collection.
   *
   * @param {number} organizationId Organization ID.
   * @param {number} collectionId Report collection ID.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteReportCollection(organizationId: number, collectionId: number): Promise<ApiResponseBase> {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is missing or incorrect [${organizationId}].`);
    }
    if (!collectionId || collectionId < 1 || isNaN(collectionId)) {
      return this.reject(`Collection ID is missing or incorrect [${collectionId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.reportsAdminApi.deleteReportCollection(organizationId, collectionId));
  }

  // #endregion

}
